"use client"

import {
	Cross1Icon,
	ChevronDownIcon,
	HamburgerMenuIcon,
	ChevronRightIcon,
} from "@radix-ui/react-icons"
import { Copy, Check } from "lucide-react"
import copy from "copy-to-clipboard"
import parse from "html-react-parser"
import DOMPurify from "dompurify"
import type React from "react"
import { useEffect, useState, useCallback } from "react"

interface TOCItem {
	id: string
	text: string
	level: number
	children: TOCItem[]
}

const TableOfContents: React.FC<{ content: string }> = ({ content }) => {
	const [toc, setToc] = useState<TOCItem[]>([])
	const [processedContent, setProcessedContent] = useState("")

	const handleCopy = useCallback((text: string, button: HTMLButtonElement) => {

		const success = copy(text, {
			debug: true,
			format: "text/plain",
		})

		if (success) {
			const originalHTML = button.innerHTML
			button.innerHTML =
				'<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="h-4 w-4 text-green-400"><path d="M20 6 9 17l-5-5"/></svg>'
			button.title = "Copied!"

			setTimeout(() => {
				button.innerHTML = originalHTML
				button.title = "Copy code"
			}, 2000)
		} else {
	
			button.title = "Failed to copy"
			setTimeout(() => {
				button.title = "Copy code"
			}, 2000)
		}
	}, [])

	useEffect(() => {
		const parseAndProcessContent = (content: string): [TOCItem[], string] => {
			const parser = new DOMParser()
			const doc = parser.parseFromString(content, "text/html")
			const tocItems: TOCItem[] = []
			const stack: TOCItem[] = []

			const processNode = (node: Element) => {
				const tagName = node.tagName.toLowerCase()
				if (["h1", "h2", "h3", "h4", "h5", "h6"].includes(tagName)) {
					const level = Number.parseInt(tagName.charAt(1))
					const text = node.textContent || ""
					const id =
						node.id || `heading-${Math.random().toString(36).slice(2, 11)}`
					node.id = id

					// Add margin classes based on heading level
					const marginClasses = {
						1: ["mt-12", "mb-6"],
						2: ["mt-10", "mb-5"],
						3: ["mt-8", "mb-4"],
						4: ["mt-6", "mb-3"],
						5: ["mt-5", "mb-2"],
						6: ["mt-4", "mb-2"]
					}[level] || ["mt-4", "mb-2"]

					for (const className of marginClasses) {
						node.classList.add(className)
						node.classList.add('font-bold')
					}

					const item: TOCItem = { id, text, level, children: [] }

					while (stack.length > 0 && stack[stack.length - 1].level >= level) {
						stack.pop()
					}

					if (stack.length === 0) {
						tocItems.push(item)
					} else {
						stack[stack.length - 1].children.push(item)
					}

					stack.push(item)
				} else if (tagName === "pre") {
					// Add copy button container for code blocks
					const codeElement = node.querySelector("code")
					if (codeElement) {
						// Add hljs class if not present
						if (!node.classList.contains("hljs")) {
							node.classList.add("hljs")
						}
						node.classList.add("relative", "group", "p-4", "overflow-x-auto","border","border-gray-200","rounded-lg")

						// Create copy button wrapper
						const wrapper = doc.createElement("div")
						wrapper.className = "absolute right-2 top-2 z-10"

						// Create copy button
						const copyButton = doc.createElement("button")
						copyButton.setAttribute("type", "button")
						copyButton.className =
							"copy-button rounded-md bg-slate-700/50 p-2 text-white shadow-sm hover:bg-slate-700/70 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 transition-all duration-200 backdrop-blur-sm opacity-0 group-hover:opacity-100"
						copyButton.innerHTML =
							'<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="h-4 w-4 text-white"><rect width="14" height="14" x="8" y="8" rx="2" ry="2"/><path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2"/></svg>'
						copyButton.title = "Copy code"

						// Store the code content as a data attribute
						const codeText = codeElement.textContent || ""
						copyButton.setAttribute("data-code", codeText)

						wrapper.appendChild(copyButton)
						node.insertBefore(wrapper, node.firstChild)
					}
				} else if (tagName === "table") {
					node.className =
						"my-4 border-collapse table-auto w-full text-sm border border-slate-200"

					const thead = node.querySelector("thead")
					if (thead) {
						thead.className = "bg-slate-50"
						for (const th of Array.from(thead.querySelectorAll("th"))) {
							th.className =
								"border border-slate-200 font-medium p-4 text-slate-700 text-left"
						}
					}

					const tbody = node.querySelector("tbody")
					if (tbody) {
						tbody.className = "bg-white"
						for (const tr of Array.from(tbody.querySelectorAll("tr"))) {
							tr.className = "hover:bg-slate-50 border-b border-slate-200"
							for (const cell of Array.from(tr.querySelectorAll("th, td"))) {
								cell.className = "border-x border-slate-200 p-4 text-slate-600"
							}
						}
					}
				} else if (tagName === "ol" || tagName === "ul") {
					node.className =
						tagName === "ol" ? "list-decimal pl-6 my-4" : "list-disc pl-6 my-4"
					for (const li of Array.from(node.querySelectorAll("li"))) {
						li.className = "mb-2"
					}
				} else if (tagName === "hr") {
					node.className = "my-4 border-t border-gray-200"
				} else if (tagName === "a") {
					// Highlight links with modern styling
					node.className = "text-blue-600 hover:text-blue-800 underline decoration-2 underline-offset-2 hover:decoration-blue-800 transition-colors duration-200 font-medium"
				}

				for (const child of Array.from(node.children)) {
					processNode(child)
				}
			}

			processNode(doc.body)

			// Add wrapper for iframes
			const iframes = doc.getElementsByTagName("iframe")
			for (const iframe of Array.from(iframes)) {
				const wrapper = doc.createElement("div")
				wrapper.className = "relative aspect-video w-full overflow-hidden rounded-lg my-4"
				iframe.parentNode?.insertBefore(wrapper, iframe)
				wrapper.appendChild(iframe)
				
				// Update iframe styles
				iframe.style.position = "absolute"
				iframe.style.top = "0"
				iframe.style.left = "0"
				iframe.style.width = "100%"
				iframe.style.height = "100%"
				iframe.style.border = "0"
			}

			// Configure DOMPurify to allow YouTube iframes
			DOMPurify.setConfig({
				ADD_TAGS: ["iframe"],
				ADD_ATTR: [
					"allow",
					"allowfullscreen",
					"frameborder",
					"scrolling",
					"src",
					"style",
					"width",
					"height",
					"data-aspect-width",
					"data-aspect-height"
				]
			})

			return [tocItems, doc.body.innerHTML]
		}

		const [newToc, newContent] = parseAndProcessContent(content)
		setToc(newToc)
		setProcessedContent(DOMPurify.sanitize(newContent))
	}, [content])

	useEffect(() => {
		const handleClick = (event: MouseEvent) => {
			const button = event.currentTarget as HTMLButtonElement
			const code = button.getAttribute("data-code")
			if (code) {
				handleCopy(code, button)
			}
		}

		const copyButtons = Array.from(
			document.querySelectorAll<HTMLButtonElement>(".copy-button"),
		)
		// console.log("Found copy buttons:", copyButtons.length)

		for (const button of copyButtons) {
			button.addEventListener("click", handleClick)
		}

		return () => {
			for (const button of copyButtons) {
				button.removeEventListener("click", handleClick)
			}
		}
	}, [processedContent, handleCopy])

	const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set())
	const [isTocVisible, setIsTocVisible] = useState(true)
	const toggleTocVisibility = () => {
		setIsTocVisible(!isTocVisible)
	}
	const toggleExpand = (id: string) => {
		setExpandedItems((prev) => {
			const newSet = new Set(prev)
			if (newSet.has(id)) {
				newSet.delete(id)
			} else {
				newSet.add(id)
			}
			return newSet
		})
	}
	const CHILDREN_THRESHOLD = 1

	const renderTOCItem = (item: TOCItem, depth = 0) => {
		const isExpanded = expandedItems.has(item.id)
		const hasChildren = item.children.length > 0
		const hasMoreChildrenThanThreshold =
			item.children.length > CHILDREN_THRESHOLD

		return (
			<li key={item.id} className={`my-1 ${depth > 0 ? "ml-4" : ""}`}>
				<div className="flex items-center">
					{hasChildren && !hasMoreChildrenThanThreshold && (
						<button
							onClick={() => toggleExpand(item.id)}
							type={"button"}
							className="mr-2 rounded-full p-1 hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
						>
							{isExpanded ? (
								<ChevronDownIcon className="text-gray-500" />
							) : (
								<ChevronRightIcon className="text-gray-500" />
							)}
						</button>
					)}
					<a
						href={`#${item.id}`}
						className="text-gray-700 transition-colors duration-200 hover:text-indigo-600"
					>
						{item.text}
					</a>
				</div>
				{hasChildren && (isExpanded || hasMoreChildrenThanThreshold) && (
					<ul className="mt-2">
						{item.children.map((child) => renderTOCItem(child, depth + 1))}
					</ul>
				)}
			</li>
		)
	}

	return (
		<>
			{toc.length > 0 && (
				<nav className="table-of-contents mb-8 w-full rounded-lg border bg-downy-50 p-2 md:w-3/4">
					<div className="flex items-center gap-3">
						<button
							onClick={toggleTocVisibility}
							className="rounded-full p-2 hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
							aria-label={
								isTocVisible
									? "Collapse table of contents"
									: "Expand table of contents"
							}
							type={"button"}
						>
							{isTocVisible ? (
								<Cross1Icon className="text-gray-500" />
							) : (
								<HamburgerMenuIcon className="text-gray-500" />
							)}
						</button>
						<h2 className="font-bold text-2xl text-gray-800">Mục lục</h2>
					</div>
					<ul className="space-y-2">
						{isTocVisible && toc.map((item) => renderTOCItem(item))}
					</ul>
				</nav>
			)}
			<div className="prose prose-lg max-w-none whitespace-pre-line text-base">
				{parse(processedContent)}
			</div>
		</>
	)
}

export default TableOfContents
